import { handleToast } from 'helpers/handleToast';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { sanitize } from 'utils/sanitize';
import { sanitizeObjectHtml } from 'utils/htmlSanitize';
import { formatErrorResponse, validate } from 'utils/zodValidation';
import { adminDialogSchema } from './AdminDialog.schema';
import { getRoles, postAdmin, putAdmin } from './AdminDialog.services';
import { AdminDialogProps } from './AdminDialog.types';

const DEFAULT_ADMIN = {
	name: '',
	email: '',
	roleId: '',
};

export const useAdminDialog = ({
	open,
	setOpen,
	user,
	onSuccess,
}: AdminDialogProps) => {
	const [fieldsWithErrors, setFieldsWithErrors] = useState<
		Record<string, string>
	>({});
	const [admin, setAdmin] = useState<any>(DEFAULT_ADMIN);
	const [isRequestLoading, setRequestLoading] = useState<boolean>(false);
	const [isRolesLoading, setRolesLoading] = useState<boolean>(false);
	const [roles, setRoles] = useState<any[]>([]);

	const isEditingAdmin = useMemo(() => Boolean(user?.id), [user]);

	const handleChangeAdmin = (
		event: ChangeEvent<HTMLInputElement> | undefined,
	) => {
		if (event) {
			const { name, value } = event.target;
			setAdmin({ ...admin, [name]: value });
		}
	};

	const handleSaveAdmin = async () => {
		setFieldsWithErrors({});
		setRequestLoading(true);

		try {
			const sanitizedAdmin = sanitizeObjectHtml(admin);

			await validate(adminDialogSchema, sanitize(sanitizedAdmin));

			if (isEditingAdmin) {
				await putAdmin(sanitizedAdmin);
			} else {
				await postAdmin(sanitizedAdmin);
			}

			handleToast(
				'success',
				`Usuario ${isEditingAdmin ? 'actualizado' : 'creado'} exitosamente`,
				`${
					isEditingAdmin
						? 'Se ha enviado un correo electrónico con los pasos a seguir'
						: undefined
				} `,
			);
			onSuccess?.();
			setOpen(false);
		} catch (error: any) {
			if (error.zodError) {
				const errors = formatErrorResponse(error.zodError);
				setFieldsWithErrors(errors);
				handleToast('error', 'Rellena los campos marcados');
				console.error(errors);
			} else {
				handleToast('error', 'Hubo un error al intentar realizar esta acción');
			}
		} finally {
			setRequestLoading(false);
		}
	};

	const fetchRoles = async () => {
		setRolesLoading(true);
		try {
			const { data } = await getRoles();
			setRoles(data.content);
		} catch (error) {
			handleToast(
				'error',
				'Hubo un error al cargar los tipos de perfil de usuario',
			);
		} finally {
			setRolesLoading(false);
		}
	};

	useEffect(() => {
		fetchRoles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!open) return;

		setFieldsWithErrors({});
		setAdmin(
			user
				? {
						...user,
						roleId: user.role?.id,
						active: user.status !== 'disabled',
				  }
				: DEFAULT_ADMIN,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, user]);

	return {
		isEditingAdmin,
		fieldsWithErrors,
		isRequestLoading,
		admin,
		setAdmin,
		handleChangeAdmin,
		roles,
		isRolesLoading,
		handleSaveAdmin,
	};
};
